import React from "react";
import useDivingPermitForm from "../../../hooks/useDivingPermitForm";
import { PermitFormProps } from "../../../types/forms/Permits/PermitFormProps";
import LoadingSpinner from "../../Common/LoadingSpinner";
import PermitHeader from "./PermitHeader";
import { Location, PermitType, WorkOrigin } from "../../../common/enums";
import DangerAlert from "../../Common/DangerAlert";
import PermitActionBar from "./PermitActionBar";
import RadioButton from "../../Common/RadioButton";
import SignatureDisplay from "../../Signatures/SignatureDisplay";
import SignatureModal from "../../Signatures/SignatureModal";
import TextInput from "../../Common/TextInput";
import SearchDropdown from "../../Common/SearchDropdown";
import DateTimeInput from "../../Common/DateTimeInput";
import RemindMe from "./RemindMe";
import TextArea from "../../Common/TextArea";
import LabelCheckbox from "../../Common/LabelCheckbox";
import ParticipantsList from "./ParticipantsList";
import AdminComment from "./AdminComment";
import Button from "../../Common/Button";
import AcceptUnsavedChangesModal from "./AcceptUnsavedChangesModal";
import RejectPermitModal from "../RejectPermitModal";
import Link from "../../Common/Link";
import PositionPicker from "./PositionPicker";
import { permitContentVersions } from "../../../types/WorkPermit";
import resourceUtils from "../../../common/resourceUtils";
import InformationAlert from "../../Common/InformationAlert";

function DivingPermitForm(props: PermitFormProps) {
    const formManager = useDivingPermitForm(props);

    const { formState, storedPermit, department, isNewPermit, formStateChangeHandler } = formManager;
    const { resources } = props;

    if (formManager.isLoading) {
        return <LoadingSpinner />;
    }

    const permitNameResource = resourceUtils.getPermitNameResource(
        PermitType.DivingPermit,
        formManager.contentVersion,
        resources
    );

    return (
        <>
            <div className="mt-5 mb-5">
                <div className="container">
                    <div className="mb-5">
                        <PermitHeader
                            formState={formState}
                            permitDocument={storedPermit}
                            permitNameResource={permitNameResource}
                            permitType={PermitType.DivingPermit}
                            resources={resources}
                        />
                    </div>

                    {!!storedPermit && formManager.showRejectionComment ? (
                        <DangerAlert>{storedPermit.rejectionComment}</DangerAlert>
                    ) : null}

                    {formManager.contentVersion >= permitContentVersions.version_2026_04 ?
                        <InformationAlert>
                            {resources.get("DivingPermitInfoText_2026_04")}
                        </InformationAlert>
                    
                    : null}

                    {storedPermit ? (
                        <PermitActionBar
                            acceptPermitClickHandler={formManager.acceptButtonClickHandler}
                            copyPermitClickHandler={formManager.copyButtonClickHandler}
                            rejectPermitClickHandler={formManager.rejectPermitClickHandler}
                            renewPermitClickHandler={formManager.renewPermitClickHandlerAsync}
                            department={department}
                            permitDocument={storedPermit}
                            resources={props.resources}
                            requestInProgress={formManager.requestInProgress}
                        />
                    ) : null}

                    <form onSubmit={formManager.submitHandler}>
                        <div className="d-flex mt-3">
                            <span className="fw-bold me-2">{resources.get("DivingLocation")}</span>

                            {isNewPermit || formState.divingLocation.value === Location.General ? (
                                <RadioButton
                                    checked={formState.divingLocation.value === Location.General}
                                    inputId="diving-lcation__general"
                                    label={resources.get("General")}
                                    name="diving-location"
                                    onSelect={() =>
                                        formManager.formStateChangeHandler("divingLocation", Location.General)
                                    }
                                    containerClassName="me-3"
                                    readOnly={!isNewPermit}
                                />
                            ) : null}

                            {isNewPermit || formState.divingLocation.value === Location.APMT ? (
                                <RadioButton
                                    checked={formState.divingLocation.value === Location.APMT}
                                    inputId="diving-lcation__apmt"
                                    label={resources.get("APMT")}
                                    name="diving-location"
                                    onSelect={() => formManager.formStateChangeHandler("divingLocation", Location.APMT)}
                                    containerClassName="me-3"
                                    readOnly={!isNewPermit}
                                />
                            ) : null}

                            {isNewPermit || formState.divingLocation.value === Location.EnergyHarbour ? (
                                <RadioButton
                                    checked={formState.divingLocation.value === Location.EnergyHarbour}
                                    inputId="diving-location__energy-harbour"
                                    label={resources.get("EnergyHarbour")}
                                    name="diving-location"
                                    onSelect={() =>
                                        formManager.formStateChangeHandler("divingLocation", Location.EnergyHarbour)
                                    }
                                    containerClassName="me-3"
                                    readOnly={!isNewPermit}
                                />
                            ) : null}
                        </div>

                        <div className="mt-3 mb-3">
                            <h1 className="h6">{resources.get("ExtractFromPortOrder")}</h1>
                            <p>{resources.get("DivingExctract")}</p>

                            <h6>{resources.get("BasicRequirements")}</h6>
                            <ul>
                                <li>{resources.get("WrittenPermissionRequired")}</li>
                                <li>{resources.get("DivingLeaderInformsPCDaily")}</li>
                                <li>
                                    {resources.get("InformPCBeforeStartAndEnd1")}{" "}
                                    <u>{resources.get("InformPCBeforeStartAndEnd2")}</u>
                                    {resources.get("InformPCBeforeStartAndEnd3")}{" "}
                                    <u>{resources.get("InformPCBeforeStartAndEnd4")}</u>
                                    {resources.get("InformPCBeforeStartAndEnd5")}
                                </li>
                                <li>{resources.get("ContinousMonitoringOfVHF13")}</li>
                                <li>
                                    {resources.get("RulesWillBeFollowed1")}{" "}
                                    <u>{resources.get("RulesWillBeFollowed2")}</u>
                                    {resources.get("RulesWillBeFollowed3")}
                                </li>
                                <li>{resources.get("IfWorkFromRaftOrPlatform")}</li>
                                <li>{resources.get("IfStrongWeather")}</li>
                            </ul>
                            <i>
                                {resources.get("PersonalDataWarning")}{" "}
                                <a href="https://www.goteborgshamn.se">www.goteborgshamn.se</a>
                            </i>
                        </div>

                        {formManager.showAPMTConditions ? (
                            <div
                                className="p-3 rounded mt-3 mb-3"
                                style={{
                                    backgroundColor: "#FFF9EA"
                                }}
                            >
                                <h1 className="h6">{resources.get("IfWorkIsPerformedAtAPMT")}</h1>
                                <ol>
                                    <li>
                                        <div>{resources.get("ContactAPTMOrg")}</div>
                                        <div>{resources.get("APMTContactDepartment")}</div>
                                        <div>{resources.get("ContactPerson")}: Dennis Dragovic</div>
                                        <div>
                                            {resources.get("Email")}:{" "}
                                            <a href="mailto:dennis.dragovic@apmterminals.com">
                                                dennis.dragovic@apmterminals.com
                                            </a>
                                        </div>
                                        <div>{resources.get("Telephone")}: +46707910850</div>
                                    </li>
                                    <li className="mt-3">{resources.get("GeneralWorkPermitIssuedByAPMT")}</li>
                                    <li className="mt-3">{resources.get("IfWorkIsPerformedNearWaterAPMT")}</li>
                                    <li className="mt-3">
                                        <div>{resources.get("SafetyInductionPerformedByEachPerson")}</div>
                                        <div>
                                            {resources.get("Web")}:{" "}
                                            <a href="http://www.safeatapmterminals.se">www.safeatapmterminals.se</a>
                                        </div>
                                        <div>{resources.get("CourseCode")}: CN198145 (Svenska), CN10170E (English)</div>
                                        <div>
                                            {resources.get("Registration")}:{" "}
                                            {resources.get("PerformedOnFronPageOfWebPortal")}
                                        </div>
                                    </li>
                                </ol>

                                <h1 className="h6 mt-5">{resources.get("APMTInformationConfirmationSignature")}</h1>

                                <SignatureDisplay
                                    resources={resources}
                                    signatureManager={formManager.apmtSignatureManager}
                                    signatureLabel={""}
                                    containerClassName="mb-3 mt-3"
                                />
                            </div>
                        ) : null}

                        {formManager.showEnergyHarbourConditions ? (
                            <div
                                className="p-3 rounded mt-3 mb-3"
                                style={{
                                    backgroundColor: "#FFF9EA"
                                }}
                            >
                                <h6>{resources.get("DivingPermitEHAreProvidedOnTerms")}</h6>
                                <p>{resources.get("WorkPermitEHCanBeCreatedBy")}</p>
                                <TextInput
                                    containerClassName={"w-50"}
                                    inputId="energy-harbour-permit-sequence-identifier"
                                    labelText={resources.get("IfAnEHPermitAlreadyExists")}
                                    onChange={(s) => formStateChangeHandler("energyHarbourSequenceIdentifier", s)}
                                    value={formState.energyHarbourSequenceIdentifier.value}
                                    errorMessage={formState.energyHarbourSequenceIdentifier.errorMessage}
                                    readOnly={!formManager.canManageContent}
                                />

                                {formManager.canManageContent ? (
                                    <SearchDropdown
                                        inputId="energy-harbour-permit-search-input"
                                        searchLabel={resources.get("SearchLabel")}
                                        placeHolder={resources.get("SearchPlaceholder")}
                                        handler={formManager.energyHarbourPermitSearchHandler}
                                        noSearchResultsText={resources.get("NoSearchResults")}
                                        ref={formManager.energyHarbourPermitSearchHandler.searchSectionRef}
                                    />
                                ) : null}
                            </div>
                        ) : null}

                        <PositionPicker
                            canManagePositions={formManager.canManagePositions}
                            canManagePositionIsActive={formManager.canManagePositions}
                            canManagePolygons={formManager.canManagePositionPolygons}
                            canManageComments={formManager.canManagePositions}
                            onPositionAdded={formManager.addPositionPickerRowHandler}
                            onPositionChange={formManager.updatePositionPickerRowHandler}
                            onPositionRemoved={formManager.removePositionPickerRowHandler}
                            onUpdatePositionsClicked={formManager.updatePositionsClickHandlerAsync}
                            positions={formState.positions}
                            resources={props.resources}
                            showUpdatePositionsButton={formManager.showUpdatePositionsButton}
                            canManageCategories={false}
                            selectedCategories={[]}
                            showActiveStatus={true}
                            showCategories={false}
                        />

                        <div className="row mt-3">
                            <DateTimeInput
                                defaultValue={formState.startDate.value ?? new Date()}
                                id="start-date"
                                label={resources.get("StartDateTime")}
                                onBlur={formManager.startDateHandler.blurHandler}
                                className="col-md-6"
                                errorMessage={formState.startDate.errorMessage}
                                ref={formManager.startDateHandler.ref}
                                readOnly={!formManager.canManageContent}
                            />
                            <DateTimeInput
                                defaultValue={formState.endDate.value ?? new Date()}
                                id="end-date"
                                label={resources.get("EndDateTime")}
                                onBlur={formManager.endDateHandler.blurHandler}
                                className="col-md-6"
                                errorMessage={formState.endDate.errorMessage}
                                minValue={formState.startDate.value}
                                ref={formManager.endDateHandler.ref}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="mt-3">
                            <RemindMe
                                formState={formState}
                                onChange={formManager.registerFormStateChange}
                                resources={resources}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.entrepreneur.value}
                                errorMessage={formState.entrepreneur.errorMessage}
                                inputId="entrepreneur"
                                labelText={props.resources.get("PerformingDivingCompany")}
                                onChange={(s) => formStateChangeHandler("entrepreneur", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                value={formState.divingCompanyNr.value}
                                errorMessage={formState.divingCompanyNr.errorMessage}
                                inputId="diving-company-nr"
                                labelText={props.resources.get("DivingCompanyNr")}
                                onChange={(s) => formStateChangeHandler("divingCompanyNr", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.projectLeader.name}
                                errorMessage={formState.projectLeader.nameErrorMessage}
                                inputId="project-leader__name"
                                labelText={props.resources.get("ProjectLeader")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("projectLeader", {
                                        ...formState.projectLeader,
                                        name: s
                                    })
                                }
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                value={formState.projectLeader.phoneNumber}
                                errorMessage={formState.projectLeader.phoneNumberErrorMessage}
                                inputId="project-leader__phone-number"
                                labelText={props.resources.get("ProjectLeaderPhoneNumber")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("projectLeader", {
                                        ...formState.projectLeader,
                                        phoneNumber: s
                                    })
                                }
                                containerClassName="col-md-6"
                                placeHolder="e.g. +46705555555"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.divingLeader.name}
                                errorMessage={formState.divingLeader.nameErrorMessage}
                                inputId="diving-leader__name"
                                labelText={props.resources.get("DivingLeader")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("divingLeader", {
                                        ...formState.divingLeader,
                                        name: s
                                    })
                                }
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                value={formState.divingLeader.phoneNumber}
                                errorMessage={formState.divingLeader.phoneNumberErrorMessage}
                                inputId="diving-leader__phone-number"
                                labelText={props.resources.get("DivingLeaderPhoneNumber")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("divingLeader", {
                                        ...formState.divingLeader,
                                        phoneNumber: s
                                    })
                                }
                                containerClassName="col-md-6"
                                placeHolder="e.g. +46705555555"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <TextArea
                            value={formState.description.value}
                            errorMessage={formState.description.errorMessage}
                            labelText={resources.get("PurposeOfDive")}
                            onChange={(s) => formStateChangeHandler("description", s)}
                            textAreaId="description"
                            containerClassName="mt-3"
                            readOnly={!formManager.canManageContent}
                        />

                        <div className="d-flex justify-content-between mt-3">
                            <LabelCheckbox
                                checked={formState.VHFIsAvailable.value}
                                id="vhf-is-available"
                                label={resources.get("VHFIsAvailable")}
                                onChange={(c) => formStateChangeHandler("VHFIsAvailable", c)}
                                labelClassName="fw-bold"
                                readOnly={!formManager.canManageContent}
                            />

                            <div className="d-flex">
                                <span className="fw-bold me-2">{resources.get("DivingFrom")}</span>
                                <RadioButton
                                    checked={formState.divingFrom.value === WorkOrigin.Quay}
                                    inputId="diving-from__quay"
                                    label={resources.get("Quay")}
                                    name="diving-from"
                                    onSelect={() => formManager.formStateChangeHandler("divingFrom", WorkOrigin.Quay)}
                                    containerClassName="me-3"
                                    readOnly={!formManager.canManageContent}
                                />
                                <RadioButton
                                    checked={formState.divingFrom.value === WorkOrigin.Boat}
                                    inputId="diving-from__boat"
                                    label={resources.get("Boat")}
                                    name="diving-from"
                                    onSelect={() => formManager.formStateChangeHandler("divingFrom", WorkOrigin.Boat)}
                                    containerClassName="me-3"
                                    readOnly={!formManager.canManageContent}
                                />
                                <RadioButton
                                    checked={formState.divingFrom.value === WorkOrigin.Raft}
                                    inputId="diving-from__raft"
                                    label={resources.get("Raft")}
                                    name="diving-from"
                                    onSelect={() => formManager.formStateChangeHandler("divingFrom", WorkOrigin.Raft)}
                                    containerClassName="me-3"
                                    readOnly={!formManager.canManageContent}
                                />
                                <RadioButton
                                    checked={formState.divingFrom.value === WorkOrigin.Pram}
                                    inputId="diving-from__pram"
                                    label={resources.get("Pram")}
                                    name="diving-from"
                                    onSelect={() => formManager.formStateChangeHandler("divingFrom", WorkOrigin.Pram)}
                                    containerClassName="me-3"
                                    readOnly={!formManager.canManageContent}
                                />
                            </div>
                        </div>

                        <div className="mt-3">
                            <span className="fw-bold">{resources.get("OtherContactInformation")}</span>
                            <ParticipantsList
                                onAddParticipant={formManager.addParticipantHandler}
                                onParticipantChange={formManager.updateParticipantHandler}
                                onRemoveParticipant={formManager.removeParticipantHandler}
                                resources={resources}
                                participantListItems={formState.persons}
                                showUpdateButton={formManager.showUpdateParticipantsButton}
                                onUpdateButtonClick={formManager.updateParticipantsButtonClickHandlerAsync}
                                readOnly={!formManager.canManageParticipants}
                            />
                        </div>

                        <AdminComment
                            formElement={formState.adminComment}
                            onChange={(s) => formStateChangeHandler("adminComment", s)}
                            readonly={!formManager.canManageAdminComment}
                            onUpdateButtonClick={formManager.updateAdminCommentButtonClickHandlerAsync}
                            showUpdateButton={formManager.showUpdateAdminCommentButton}
                            resources={resources}
                            show={formManager.showAdminComment}
                            containerClassName="mt-4"
                        />

                        <SignatureDisplay
                            resources={resources}
                            signatureManager={formManager.permitProviderSignatureManager}
                            signatureLabel={resources.get("PermitProvider")}
                            containerClassName="mt-3 mb-4"
                        />
                        <div className="d-flex mt-3">
                            <div className="me-3">
                                {formManager.canCreate ? (
                                    <Button type="submit" disabled={formManager.requestInProgress}>
                                        {resources.get("Send")}
                                    </Button>
                                ) : formManager.canUpdate ? (
                                    <Button type="submit" disabled={formManager.requestInProgress}>
                                        {resources.get("Update")}
                                    </Button>
                                ) : null}
                            </div>
                            {!!storedPermit && formManager.showPrefillEnergyHarbourPermitButton ? (
                                <Link
                                    href={`/Permits/EnergyHarbourPermit?divingPermitDocumentId=${storedPermit.id}`}
                                    className="btn btn-primary"
                                >
                                    {resources.get("StartEHPermit")}
                                </Link>
                            ) : null}
                        </div>
                    </form>
                </div>
            </div>
            <SignatureModal
                resources={resources}
                SignatureManager={formManager.apmtSignatureManager}
                ref={formManager.apmtSignatureManager.signaturePadRef}
            />
            <SignatureModal
                resources={resources}
                SignatureManager={formManager.permitProviderSignatureManager}
                ref={formManager.permitProviderSignatureManager.signaturePadRef}
            />
            <AcceptUnsavedChangesModal
                show={formManager.showAcceptUnsavedChangesModal}
                onHide={() => formManager.setShowAcceptUnsavedChangesModal(false)}
                onAccept={formManager.unsavedChangesModalAcceptHandlerAsync}
                onUpdate={formManager.unsavedChangesModalUpdateHandlerAsync}
                resources={resources}
            />
            <RejectPermitModal
                onHide={() => formManager.setShowRejectPermitModal(false)}
                show={formManager.showRejectPermitModal}
                resources={resources}
                onReject={formManager.rejectPermitHandlerAsync}
            />
        </>
    );
}

export default DivingPermitForm;
